import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { RefreshCw } from "lucide-react";

import LoadingScreen from "@components/LoadingInterstitial";
import { Button } from "@components/ui/Button/Button";

import { useNmh } from "@lib/contexts/NmhContext";

export const MemberAccessBoundary = () => {
  const { t } = useTranslation();
  const {
    isEntitlementsPending,
    isEntitlementsError,
    isEntitlementsFetching,
    retryEntitlements,
  } = useNmh();
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isEntitlementsError) alertRef.current?.focus();
  }, [isEntitlementsError]);

  if (isEntitlementsError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-24px py-48px">
        <div
          role="alert"
          tabIndex={-1}
          ref={alertRef}
          className="w-full max-w-[480px] flex flex-col items-center gap-24px text-center outline-none"
        >
          <h1 className="font-mabry-pro-medium text-24px text-ui-pepper">
            {t("members.accessLoadFailed", {
              defaultValue: "We couldn't load your access.",
            })}
          </h1>
          <p className="font-mabry-pro-regular text-16px text-ui-pepper">
            {t("members.accessLoadFailedDetail", {
              defaultValue: "Please try again to open this page.",
            })}
          </p>
          <Button
            type="button"
            disabled={isEntitlementsFetching}
            onClick={retryEntitlements}
          >
            <RefreshCw size={18} aria-hidden="true" />
            {t("label.tryAgain", { defaultValue: "Try again" })}
          </Button>
        </div>
      </div>
    );
  }
  if (isEntitlementsPending) return <LoadingScreen />;

  return <Outlet />;
};
